type AosLib = {
    init: (opts?: Record<string, unknown>) => void;
    refresh?: () => void;
    refreshHard?: () => void;
};

const AUTO_AOS_ROOT = "main";
const AOS_DURATION = "600";
const AOS_DELAY_STEP = 80;
const AOS_DELAY_MAX = 320;
const AOS_RUNTIME_CLASSES = ["aos-init", "aos-animate"];
const AOS_ATTRIBUTE_NAMES = [
    "data-aos",
    "data-aos-anchor",
    "data-aos-anchor-placement",
    "data-aos-delay",
    "data-aos-duration",
    "data-aos-easing",
    "data-aos-mirror",
    "data-aos-offset",
    "data-aos-once",
];
const AOS_DESCENDANT_SELECTOR = [
    "[data-aos]",
    "[data-aos-anchor]",
    "[data-aos-anchor-placement]",
    "[data-aos-delay]",
    "[data-aos-duration]",
    "[data-aos-easing]",
    "[data-aos-mirror]",
    "[data-aos-offset]",
    "[data-aos-once]",
    ".aos-init",
    ".aos-animate",
].join(",");

const INTERACTIVE_SECTION_VALUES = new Set([
    "wwa-horizon",
    "wwa-signature",
    "cp-roadmap",
    "scale-zoom",
    "awards-wall",
    "research-framework",
    "floor-plan",
    "faculty-spotlight",
    "events-calendar-interactive",
]);

const INTERACTIVE_SELECTOR = [
    "[data-no-auto-aos]",
    "[data-anim]",
    "[data-swiper]",
    ".swiper",
    ".swiper-wrapper",
    ".swiper-slide",
    ".fade-up",
    ".list-js",
    ".blur-js",
    ".clip-down-up",
    ".scale-img",
    ".move-img",
    ".cs-option",
    ".mcs-option",
].join(",");

const GROUP_SELECTOR = [
    "header",
    "[class*='__head']",
    "[class*='__copy']",
    "[class*='__intro']",
].join(",");

const CONTENT_SELECTOR = [
    "h1",
    "h2",
    "h3",
    "[class*='__title']",
    "[class*='__headline']",
    "[class*='__lead']",
    "[class*='__body']",
    "[class*='__desc']",
    "[class*='__text']",
    "[class*='__lines'] > p",
].join(",");

const ITEM_CONTAINER_SELECTOR = [
    "ul",
    "ol",
    "[class*='__grid']",
    "[class*='__list']",
    "[class*='__items']",
    "[class*='__cards']",
    "[class*='__row']",
].join(",");

const ITEM_SELECTOR = [
    ":scope > li",
    ":scope > article",
    ":scope > .ui-card",
    ":scope > [class*='__card']",
    ":scope > [class*='__item']",
    ":scope > [class*='__step']",
].join(",");
const SECTION_TITLE_STAGGER_SELECTOR = '[data-anim="section-title-stagger"]';

const SMALL_LIST_PARTS = [
    "__actions",
    "__badges",
    "__breadcrumbs",
    "__controls",
    "__cta",
    "__field",
    "__filters",
    "__form",
    "__input",
    "__meta",
    "__nav",
    "__pagination",
    "__select",
    "__social",
    "__tabs",
];

export class AosInitializer {
    static init(): void {
        const aos = (window as unknown as { AOS?: AosLib }).AOS;
        if (!aos?.init) return;

        if (AosInitializer.prefersReducedMotion()) {
            aos.init({ disable: true });
            return;
        }

        AosInitializer.applyAutoFadeUp();

        aos.init({
            duration: 600,
            easing: 'ease-out-quart',
            once: true,
            offset: 80,
        });

        window.addEventListener(
            "load",
            () => {
                aos.refresh?.();
            },
            { once: true },
        );
    }

    static refresh(): void {
        const aos = (window as unknown as { AOS?: AosLib }).AOS;
        if (!aos?.init || AosInitializer.prefersReducedMotion()) return;

        AosInitializer.applyAutoFadeUp();
        requestAnimationFrame(() => {
            (aos.refreshHard ?? aos.refresh)?.call(aos);
        });
    }

    private static applyAutoFadeUp(): void {
        const root = document.querySelector<HTMLElement>(AUTO_AOS_ROOT);
        if (!root) return;

        root.querySelectorAll<HTMLElement>("section").forEach((section) => {
            if (AosInitializer.shouldSkipSection(section)) return;

            section.querySelectorAll<HTMLElement>(GROUP_SELECTOR).forEach((group) => {
                if (group.closest("section") !== section) return;
                AosInitializer.mark(group);
            });

            section.querySelectorAll<HTMLElement>(CONTENT_SELECTOR).forEach((content) => {
                if (content.closest("section") !== section) return;
                if (AosInitializer.isSmallList(content)) return;
                AosInitializer.mark(content);
            });

            section.querySelectorAll<HTMLElement>(ITEM_CONTAINER_SELECTOR).forEach((container) => {
                if (container.closest("section") !== section) return;
                if (AosInitializer.shouldSkipNode(container)) return;
                if (AosInitializer.isSmallList(container)) return;

                const items = Array.from(container.querySelectorAll<HTMLElement>(ITEM_SELECTOR))
                    .filter((item) => item.parentElement === container)
                    .filter((item) => !AosInitializer.shouldSkipNode(item));

                items.forEach((item, index) => {
                    AosInitializer.mark(item, AosInitializer.delayFor(index));
                });
            });

            AosInitializer.normalizeNestedAos(section);
        });
    }

    private static shouldSkipSection(section: HTMLElement): boolean {
        const sectionKey = section.dataset.section;
        if (sectionKey && INTERACTIVE_SECTION_VALUES.has(sectionKey)) return true;
        return AosInitializer.shouldSkipNode(section, true);
    }

    private static shouldSkipNode(
        node: HTMLElement,
        ignoreSectionTitleDescendant = false,
    ): boolean {
        if (node.hasAttribute("data-aos")) return true;
        if (AosInitializer.hasAosAncestor(node)) return true;
        if (node.closest("[data-no-auto-aos]")) return true;
        if (node.closest(INTERACTIVE_SELECTOR)) return true;
        if (
            !ignoreSectionTitleDescendant &&
            node.querySelector(SECTION_TITLE_STAGGER_SELECTOR)
        ) return true;

        const interactiveSection = node.closest<HTMLElement>("[data-section]");
        const sectionKey = interactiveSection?.dataset.section;
        return Boolean(sectionKey && INTERACTIVE_SECTION_VALUES.has(sectionKey));
    }

    private static hasAosAncestor(node: HTMLElement): boolean {
        const parentAos = node.parentElement?.closest("[data-aos]");
        if (!parentAos) return false;
        const currentSection = node.closest("section");
        return parentAos.closest("section") === currentSection;
    }

    private static mark(node: HTMLElement, delay?: string): void {
        if (AosInitializer.shouldSkipNode(node)) return;
        node.setAttribute("data-aos", "fade-up");
        node.setAttribute("data-aos-duration", AOS_DURATION);
        if (delay !== undefined) {
            node.setAttribute("data-aos-delay", delay);
        }
    }

    private static normalizeNestedAos(section: HTMLElement): void {
        section.querySelectorAll<HTMLElement>("[data-aos]").forEach((parent) => {
            parent.querySelectorAll<HTMLElement>(AOS_DESCENDANT_SELECTOR).forEach((child) => {
                AosInitializer.clearAos(child);
            });
        });
    }

    private static clearAos(node: HTMLElement): void {
        AOS_ATTRIBUTE_NAMES.forEach((attribute) => {
            node.removeAttribute(attribute);
        });
        AOS_RUNTIME_CLASSES.forEach((className) => {
            node.classList.remove(className);
        });
    }

    private static delayFor(index: number): string {
        return String(Math.min(index * AOS_DELAY_STEP, AOS_DELAY_MAX));
    }

    private static isSmallList(node: HTMLElement): boolean {
        const className = node.className;
        if (typeof className !== "string") return false;
        return SMALL_LIST_PARTS.some((part) => className.includes(part));
    }

    private static prefersReducedMotion(): boolean {
        return (
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
    }
}
